import { useEffect, useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiLogOut, FiAlertCircle, FiLoader, FiMenu, FiX, FiGrid, FiList, FiUser, FiPlay, FiRefreshCw } from 'react-icons/fi';
import { HiMiniChartBar } from "react-icons/hi2";
import { MdViewModule } from "react-icons/md";
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import Footer from '../components/Footer';
import LOGO from '../assets/fav.png';

// Server configuration - Add absolute URLs to debug potential path issues
const HOSTINGER_API_ENDPOINT = 'https://academy.lernvent.com/videos.json';
const VIDEO_BASE_URL = 'https://academy.lernvent.com/videos/';
const THUMBNAIL_BASE_URL = 'https://academy.lernvent.com/thumbnails/';
// Use an absolute URL for fallback thumbnail to ensure it loads
const FALLBACK_THUMBNAIL = 'https://academy.lernvent.com/thumbnails/vid1.png';

export default function Dashboard() {
  const [videos, setVideos] = useState([]); 
  const [userIp, setUserIp] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [userProfile, setUserProfile] = useState(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch user IP
        const ipResponse = await fetch('https://api.ipify.org?format=json');
        const ipData = await ipResponse.json();
        setUserIp(ipData.ip);
        
        // Fetch user profile from Firebase
        if (user?.uid) {
          const userDocRef = doc(db, "users", user.uid);
          const userDocSnap = await getDoc(userDocRef);
          
          if (userDocSnap.exists()) {
            setUserProfile(userDocSnap.data());
          }
          
          // Fetch videos
          await fetchVideos();
        }
      } catch (error) {
        setError('Failed to load data. Please try again later.');
        console.error('Dashboard error:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [user]);

  const VideoCard = ({ video }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [errorDetails, setErrorDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [thumbnailError, setThumbnailError] = useState(false);
    const videoRef = useRef(null);
  
    // Get video URL with cache busting
    const getVideoUrl = () => {
      return `${video.url}?t=${Date.now()}`;
    };
  
    const handlePlayback = () => {
      try {
        setIsLoading(true);
        setErrorDetails(null);
        
        if (!isPlaying) {
          setIsPlaying(true);
          return;
        }
  
        videoRef.current.src = getVideoUrl();
        videoRef.current.load();
        
        videoRef.current.play().catch(error => {
          console.error('Autoplay failed:', error);
          setErrorDetails('Click to start playback');
          setIsLoading(false);
        });
      } catch (error) {
        console.error('Video initialization failed:', error);
        setErrorDetails(`Playback error: ${error.message}`);
        setIsPlaying(false);
        setIsLoading(false);
      }
    };
  
    return (
      <div 
        className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300"
        onContextMenu={(e) => e.preventDefault()} // Prevent right-click menu
      >
        <div className="relative">
          <div 
            className="relative group cursor-pointer" 
            onClick={handlePlayback}
            onContextMenu={(e) => e.preventDefault()}
          >
            {!isPlaying ? (
              <div className="w-full aspect-video bg-gray-200 flex items-center justify-center">
                {thumbnailError ? (
                  <div className="text-center p-4">
                    <div className="text-gray-400 mb-2">{video.title}</div>
                    <FiPlay className="w-12 h-12 text-gray-400 mx-auto" />
                  </div>
                ) : (
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="w-full h-full object-cover"
                    onError={() => setThumbnailError(true)}
                    loading="lazy"
                    draggable="false" // Prevent image drag
                  />
                )}
                <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="bg-white rounded-full p-4 transform scale-90 group-hover:scale-100 transition-transform duration-300">
                    <FiPlay className="w-8 h-8 text-indigo-600" />
                  </div>
                </div>
              </div>
            ) : (
              <div className="relative w-full aspect-video bg-black">
                <video
                  ref={videoRef}
                  className="w-full h-full"
                  controls
                  playsInline
                  controlsList="nodownload" // Hide download button
                  disablePictureInPicture // Disable PiP mode
                  onLoadedData={() => setIsLoading(false)}
                  onError={(e) => {
                    console.error('Video error:', e);
                    setErrorDetails('Playback failed. Please try again.');
                    setIsLoading(false);
                  }}
                >
                  <source src={getVideoUrl()} type="video/mp4" />
                  Your browser does not support HTML5 video.
                </video>
                {isLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
                    <FiRefreshCw className="w-12 h-12 text-white animate-spin mx-auto mb-2" />
                    <p className="text-white text-sm">Loading video...</p>
                  </div>
                )}
              </div>
            )}
          </div>
  
          {errorDetails && (
            <div className="absolute inset-0 bg-white/90 flex flex-col items-center justify-center p-4">
              <FiAlertCircle className="w-12 h-12 text-red-500 mb-2" />
              <p className="text-red-600 text-center text-sm mb-4">{errorDetails}</p>
              <button
                onClick={() => {
                  setIsPlaying(false);
                  setIsLoading(false);
                  setErrorDetails(null);
                }}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          )}
        </div>
  
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-1">
            {video.title}
          </h3>
          {video.description && (
            <p className="text-gray-600 text-sm line-clamp-2">{video.description}</p>
          )}
        </div>
      </div>
    );
  };
  
  // In the main Dashboard component, simplify the fetchVideos function:
  const fetchVideos = async () => {
    try {
      const response = await fetch(HOSTINGER_API_ENDPOINT);
      
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  
      const serverVideos = await response.json();
      
      const formattedVideos = serverVideos.map(video => ({
        id: video.file_id,
        title: video.file_name,
        description: video.description || 'No description available',
        url: `${VIDEO_BASE_URL}${video.file_path.split('/').pop()}`,
        thumbnail: video.thumbnail_path 
          ? `${THUMBNAIL_BASE_URL}${video.thumbnail_path.split('/').pop()}`
          : FALLBACK_THUMBNAIL,
        duration: video.duration ? Math.round(video.duration) : null,
        createdAt: new Date(video.upload_date)
      }));
  
      setVideos(formattedVideos);
      setError(null);
  
    } catch (error) {
      console.error("Video fetch error:", error);
      setError("Failed to load videos. Please try again later.");
    }
  };

  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      <div className="bg-indigo-50 p-4 rounded-full mb-4">
        <MdViewModule className="w-10 h-10 text-indigo-600" />
      </div>
      <h3 className="text-xl font-medium text-gray-900 mb-2">No videos available</h3>
      <p className="text-gray-600 text-center max-w-md mb-6">
        There are no videos available at the moment. Please check back later or contact support.
      </p>
      <button
        onClick={fetchVideos}
        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2"
      >
        <FiRefreshCw className="w-4 h-4" />
        <span>Refresh</span>
      </button>
    </div>
  );

  const navItems = [
    { icon: FiGrid, label: 'Dashboard', action: () => navigate('/dashboard') },
    { icon: MdViewModule, label: 'Modules', action: () => navigate('/modules') },
    { icon: HiMiniChartBar, label: 'Reports', action: () => navigate('/reports') },
    { icon: FiUser, label: 'Profile', action: () => navigate('/profile') },
    { icon: FiLogOut, label: 'Logout', action: logout }
  ];

  const Sidebar = () => (
    <div className="w-64 bg-white h-full shadow-xl">
      <div className="p-8 ">
  {/* Company Logo */}
  <img src={LOGO} alt="Company Logo" className="h-12 w-auto object-contain" />
  <span class="text-[#172787] text-xl font-semibold">Lernvent Academy</span>
</div>

  
      <div className="px-4">
        <div className="flex items-center p-4 bg-indigo-50 rounded-xl">
          <div className="bg-indigo-100 p-2 rounded-lg">
            <FiUser className="h-6 w-6 text-indigo-600" />
          </div>
          <div className="ml-3">
            <p className="text-sm font-medium text-gray-900 truncate">
              {userProfile?.username || user?.displayName || user?.email || "Guest"}
            </p>
            <p className="text-xs text-gray-500">IP: {userIp || "Unknown"}</p>
          </div>
        </div>
      </div>

      <nav className="mt-8 px-4">
        <div className="space-y-2">
          {navItems.map((item, index) => (
            <button
              key={index}
              onClick={item.action}
              className="flex items-center px-4 py-3 text-gray-700 hover:bg-gray-50 w-full rounded-xl transition-colors duration-200"
            >
              <item.icon className="h-5 w-5" />
              <span className="ml-3 font-medium">{item.label}</span>
            </button>
          ))}
        </div>
      </nav>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex h-screen">
        <div className="hidden lg:block">
          <Sidebar />
        </div>

        <AnimatePresence>
          {isSidebarOpen && (
            <>
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
                onClick={() => setIsSidebarOpen(false)}
              />
              <motion.div 
                className="fixed inset-y-0 left-0 z-30 lg:hidden"
                initial={{ x: -256 }}
                animate={{ x: 0 }}
                exit={{ x: -256 }}
                transition={{ type: 'tween' }}
              >
                <Sidebar />
              </motion.div>
            </>
          )}
        </AnimatePresence>

        <div className="flex-1 flex flex-col overflow-hidden">
          <header className="bg-white shadow-sm">
            <div className="px-6 py-4 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                  className="lg:hidden p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors duration-200"
                >
                  {isSidebarOpen ? <FiX className="h-6 w-6" /> : <FiMenu className="h-6 w-6" />}
                </button>
                <h1 className="text-xl font-semibold text-gray-900">Phase1 Modules</h1>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-indigo-100 text-indigo-600' : 'text-gray-600 hover:bg-gray-100'}`}
                >
                  <FiGrid className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-indigo-100 text-indigo-600' : 'text-gray-600 hover:bg-gray-100'}`}
                >
                  <FiList className="h-5 w-5" />
                </button>
                <button
                  onClick={fetchVideos}
                  className="p-2 rounded-lg text-gray-600 hover:bg-gray-100"
                  title="Refresh videos"
                >
                  <FiRefreshCw className="h-5 w-5" />
                </button>
              </div>
            </div>
          </header>

          <main className="flex-1 overflow-y-auto bg-gray-50 p-6">
            <AnimatePresence mode="wait">
              {loading ? (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex justify-center items-center h-64"
                >
                  <FiLoader className="animate-spin text-4xl text-indigo-600" />
                </motion.div>
              ) : error ? (
                <motion.div
                  key="error"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl flex items-center"
                >
                  <FiAlertCircle className="mr-3 h-5 w-5" />
                  {error}
                </motion.div>
              ) : videos.length === 0 ? (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <EmptyState />
                </motion.div>
              ) : (
                <motion.div
                  key="content"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className={`${viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6' : 'space-y-4'}`}
                >
                  {videos.map((video) => (
                    <VideoCard key={video.id} video={video} />
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </main>

          <Footer />
        </div>
      </div>
    </div>
  );
}